import React, { useEffect, useState } from 'react';
import { TrendingUp, BarChart3, Wallet, Coins } from 'lucide-react';
import api from '@/lib/api';
import { formatRp } from '@/lib/format';
import { ListSkeleton } from '@/components/Skeletons';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

function StatCard({ label, value, icon: Icon, tone }) {
  const tones = { primary: 'bg-primary/10 text-primary', success: 'bg-emerald-100 text-emerald-700', accent: 'bg-accent/10 text-accent' };
  return (
    <div className="rounded-xl border bg-card p-4">
      <span className={cn('mb-2 flex h-9 w-9 items-center justify-center rounded-lg', tones[tone])}><Icon className="h-5 w-5" /></span>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-display text-lg font-bold tabular-nums">{value}</div>
    </div>
  );
}

const MONTH_LABEL = (ym) => {
  const m = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
  const [y, mo] = ym.split('-');
  return `${m[parseInt(mo, 10) - 1]} '${y.slice(2)}`;
};

export default function Laporan() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/reports/profit').then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading || !data) return <div className="space-y-4"><h1 className="font-display text-xl font-bold">Laporan</h1><ListSkeleton /></div>;

  const maxOmzet = Math.max(1, ...data.monthly.map((m) => m.omzet));

  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 font-display text-xl font-bold"><BarChart3 className="h-5 w-5 text-primary" /> Laporan Profit</h1>
        <p className="text-sm text-muted-foreground">Estimasi & profit aktual per proyek.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <StatCard label="Total Nilai Proyek" value={formatRp(data.total_nilai)} icon={Wallet} tone="primary" />
        <StatCard label="Profit (Estimasi)" value={formatRp(data.total_profit_estimasi)} icon={TrendingUp} tone="accent" />
        <StatCard label="Profit (Aktual)" value={formatRp(data.total_profit_aktual)} icon={Coins} tone="success" />
      </div>

      {data.monthly.length > 0 && (
        <div className="rounded-xl border bg-card p-4">
          <h2 className="mb-3 font-display text-sm font-semibold">Omzet per Bulan</h2>
          <div className="flex items-end gap-3 h-40">
            {data.monthly.map((m) => (
              <div key={m.bulan} className="flex flex-1 flex-col items-center justify-end gap-1">
                <div className="text-[10px] font-semibold text-muted-foreground tabular-nums">{(m.omzet / 1000000).toFixed(1)}jt</div>
                <div className="w-full rounded-t-md bg-primary/80" style={{ height: `${(m.omzet / maxOmzet) * 100}%`, minHeight: '4px' }} />
                <div className="text-[10px] text-muted-foreground">{MONTH_LABEL(m.bulan)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border bg-card p-4">
        <h2 className="mb-3 font-display text-sm font-semibold">Profit per Proyek</h2>
        <div className="space-y-2.5">
          {data.rows.map((r) => (
            <div key={r.project_id} className="rounded-xl border bg-background p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate font-semibold">{r.nama_proyek}</div>
                  <div className="truncate text-xs text-muted-foreground">{r.customer_name}</div>
                </div>
                <Badge variant="outline" className={cn('rounded-full', r.sumber_profit === 'aktual' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-sky-200 bg-sky-50 text-sky-700')}>{r.sumber_profit === 'aktual' ? 'Aktual' : 'Estimasi'}</Badge>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                <div><div className="text-muted-foreground">Nilai</div><div className="font-semibold tabular-nums">{formatRp(r.nilai)}</div></div>
                <div><div className="text-muted-foreground">{r.biaya_aktual > 0 ? 'Biaya Aktual' : 'Biaya'}</div><div className="font-semibold tabular-nums">{r.biaya_aktual > 0 ? formatRp(r.biaya_aktual) : '-'}</div></div>
                <div><div className="text-muted-foreground">Profit ({r.margin_pct}%)</div><div className="font-semibold tabular-nums text-emerald-600">{formatRp(r.profit)}</div></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
